import { invalidateAnswerCaches, trackAnswerWrite } from '@/api/answerWrites';
import {
  ApiError,
  EMPTY_LIST,
  isGuest,
  type Lang,
  type List,
  request,
  retryable,
  withPicture,
} from '@/api/client';
import type { ExamLength } from '@/exam';
import { reportWarning } from '@/sentry';
import { readStored, writeStored } from '@/storage';
import { checked, EXAM_SESSION } from '@/api/shape';

export interface ExamAnswerOption {
  answer_index: number;
  answer_text: string | null;
}

export interface ExamQuestion {
  position: number;
  question_id: string;
  image_url: string | null;
  image_width?: number | null;
  image_height?: number | null;
  question_text: string | null;
  answers: ExamAnswerOption[];
  topic: number;
  selected_answer_index: number | null;
  correct_answer_index: number | null;
  is_correct: boolean | null;
  explanation: string | null;
}

export interface ExamSession {
  id: string;
  status: 'in_progress' | 'submitted' | 'expired';
  lang: string;
  question_count: number;
  time_limit_seconds: number;
  pass_threshold: number;
  started_at: string;
  expires_at: string;
  submitted_at: string | null;
  score_correct: number | null;
  passed: boolean | null;
  questions: ExamQuestion[];
}

export interface ExamHistoryItem {
  id: string;
  status: ExamSession['status'];
  score_correct: number | null;
  question_count: number;
  passed: boolean | null;
  started_at: string;
  submitted_at: string | null;
}

export type ExamsList = List<ExamHistoryItem>;

const fixExamMedia = (exam: ExamSession): ExamSession => ({
  ...exam,
  questions: exam.questions.map(withPicture),
});

export function startExam(lang: Lang, questionCount: ExamLength = 20): Promise<ExamSession> {
  const body = questionCount === 20 ? { lang } : { lang, question_count: questionCount };
  return request<unknown>('/exams', { method: 'POST', body: JSON.stringify(body) })
    .then(checked<ExamSession>(EXAM_SESSION, 'exam'))
    .then(fixExamMedia);
}

export function getExams(limit = 20): Promise<ExamsList> {
  if (isGuest()) return Promise.resolve(EMPTY_LIST);
  return request(`/exams?limit=${limit}`);
}

export function getExam(id: string): Promise<ExamSession> {
  return request<unknown>(`/exams/${id}`)
    .then(checked<ExamSession>(EXAM_SESSION, 'exam'))
    .then(fixExamMedia);
}

function saveExamAnswer(
  id: string,
  question_id: string,
  selected_answer_index: number,
): Promise<void> {
  return request(`/exams/${id}/answers`, {
    method: 'POST',
    body: JSON.stringify({ question_id, selected_answer_index }),
  });
}

const EXAM_PENDING_KEY = 'exam_pending';

const EXAM_PENDING_MAX = 2;

type PendingByExam = Record<string, Record<string, number>>;

function readPendingExams(): PendingByExam {
  try {
    const raw: unknown = JSON.parse(readStored(EXAM_PENDING_KEY) ?? '{}');
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return {};
    const store: PendingByExam = {};
    for (const [examId, answers] of Object.entries(raw)) {
      if (typeof answers !== 'object' || answers === null || Array.isArray(answers)) continue;
      const picks: Record<string, number> = {};
      for (const [questionId, index] of Object.entries(answers))
        if (typeof index === 'number') picks[questionId] = index;
      store[examId] = picks;
    }
    return store;
  } catch {
    return {};
  }
}

function writePendingExams(store: PendingByExam): void {
  const live = Object.entries(store).filter(([, picks]) => Object.keys(picks).length > 0);
  writeStored(EXAM_PENDING_KEY, JSON.stringify(Object.fromEntries(live.slice(-EXAM_PENDING_MAX))));
}

export function pendingExamAnswers(examId: string): Record<string, number> {
  return readPendingExams()[examId] ?? {};
}

export function countPendingExamAnswers(examId: string): number {
  return Object.keys(pendingExamAnswers(examId)).length;
}

export function clearPendingExamAnswers(examId: string): void {
  const store = readPendingExams();
  if (!(examId in store)) return;
  delete store[examId];
  writePendingExams(store);
}

function rememberExamAnswer(examId: string, questionId: string, index: number): void {
  const store = readPendingExams();
  store[examId] = { ...store[examId], [questionId]: index };
  writePendingExams(store);
}

function forgetExamAnswer(examId: string, questionId: string, index: number): void {
  const store = readPendingExams();
  const picks = store[examId];
  if (!picks || picks[questionId] !== index) return;
  delete picks[questionId];
  writePendingExams(store);
}

function isCurrentPick(examId: string, questionId: string, index: number): boolean {
  return readPendingExams()[examId]?.[questionId] === index;
}

async function pushExamAnswer(
  examId: string,
  questionId: string,
  answerIndex: number,
): Promise<void> {
  try {
    await saveExamAnswer(examId, questionId, answerIndex);
  } catch (first) {
    if (first instanceof ApiError && first.status === 401) throw first;
    if (!retryable(first)) {
      forgetExamAnswer(examId, questionId, answerIndex);
      throw first;
    }
    if (!isCurrentPick(examId, questionId, answerIndex)) return;
    try {
      await saveExamAnswer(examId, questionId, answerIndex);
    } catch (second) {
      if (!retryable(second)) forgetExamAnswer(examId, questionId, answerIndex);
      throw second;
    }
  }
  forgetExamAnswer(examId, questionId, answerIndex);
}

export function commitExamAnswer(
  examId: string,
  questionId: string,
  answerIndex: number,
): Promise<void> {
  rememberExamAnswer(examId, questionId, answerIndex);
  return pushExamAnswer(examId, questionId, answerIndex);
}

export const FLUSH_WAIT_MS = 4000;

export const SUBMIT_FLUSH_WAIT_MS = 15_000;

const owedReported = new Set<string>();

function reportStillOwed(examId: string, tried: [string, number][]): void {
  const owed = tried.filter(([questionId, index]) => isCurrentPick(examId, questionId, index));
  if (owed.length === 0) {
    owedReported.delete(examId);
    return;
  }
  if (owedReported.has(examId) || navigator.onLine === false) return;
  owedReported.add(examId);
  reportWarning('Exam answers still owed after retries', {
    exam_id: examId,
    owed: owed.length,
    tried: tried.length,
  });
}

export function flushExamAnswers(examId: string, budgetMs = FLUSH_WAIT_MS): Promise<void> {
  const owed = Object.entries(pendingExamAnswers(examId));
  if (owed.length === 0) return Promise.resolve();

  const pushed = (async () => {
    for (const [questionId, index] of owed) {
      if (!isCurrentPick(examId, questionId, index)) continue;
      try {
        await pushExamAnswer(examId, questionId, index);
        // eslint-disable-next-line no-empty
      } catch {}
    }
  })();
  void pushed.then(() => reportStillOwed(examId, owed));

  return new Promise((resolve) => {
    const timer = setTimeout(resolve, budgetMs);
    const done = () => {
      clearTimeout(timer);
      resolve();
    };
    pushed.then(done, done);
  });
}

export function submitExam(
  id: string,
  answers: { question_id: string; selected_answer_index: number }[],
): Promise<ExamSession> {
  invalidateAnswerCaches();
  return trackAnswerWrite(
    request<unknown>(`/exams/${id}/submit`, {
      method: 'POST',
      body: JSON.stringify({ answers }),
    })
      .then(checked<ExamSession>(EXAM_SESSION, 'exam'))
      .then(fixExamMedia),
  );
}
